import * as React from 'react';
import { BuildingOfficeIcon, UserGroupIcon, UserIcon, MapPinIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';

import {
  AnySocialLinkIcon,
  customImageLoader,
  FacebookIcon,
  InstagramIcon,
  LinkedInIcon,
  SpoonPlateIcon,
  TheForkLogo,
  TitTokIcon,
  TripAdvisiorLogo,
} from '@restworld/utils-common';

import { useEmployerRestaurantDetails } from './use-employer-restuarant-details';

export const useRestaurantInformation = () => {
  const { t } = useTranslation('restaurant-information-page');
  const employerRestaurantDetails = useEmployerRestaurantDetails();
  const placesInfo = employerRestaurantDetails?.gmaps_info?.info;
  return {
    baseInformationTab: {
      restaurantBasicInformation: {
        title: t('local_information'),
        data: {
          restaurantName: {
            title: t('name_of_the_venue'),
            data: employerRestaurantDetails?.restaurant_name,
            icon: <UserIcon className="w-6 h-6" />,
          },
          restaurantAddress: {
            title: t('address_ofthe_venue'),
            data: employerRestaurantDetails?.address,
            icon: <BuildingOfficeIcon className="w-6 h-6" />,
          },
          restaurantCity: {
            title: t('city_where_the_venue_is_located'),
            data: employerRestaurantDetails?.locality,
            icon: <MapPinIcon className="w-6 h-6" />,
          },
          localCovers: {
            title: t('covered_for_service'),
            data: employerRestaurantDetails?.covers_per_service_var?.label,
            icon: <SpoonPlateIcon className="w-5 h-5" />,
          },
          numberOfStaffs: {
            title: t('number_of_staff'),
            data:
              (employerRestaurantDetails?.staff_size_floor || 0) + (employerRestaurantDetails?.staff_size_kitchen || 0),
            icon: <UserGroupIcon className="w-6 h-6" />,
          },
          restaurantDescription: {
            title: t('venue_description'),
            data: employerRestaurantDetails?.brief_description,
            icon: <DocumentTextIcon className="w-5 h-5" />,
          },
          // restaurantTypes: {
          //   title: t('restaurant_types'),
          //   data: Array.isArray(placesInfo?.types) ? placesInfo.types.join(', ') : '',
          //   icon: <SwatchIcon className="w-6 h-6" />,
          // }, // removed for now
        },
      },
    },
    whoWeAreTab: {
      teammates: {
        title: t('teammates'),
        data: [],
      },
      favoriteRolesType: { title: t('preferred_types_of_roles') },
    },
    mediaTab: {
      logo: {
        title: t('venue_logo'),
        data: employerRestaurantDetails?.restaurant_logo,
      },
      pictures: { title: 'Media', data: employerRestaurantDetails?.pictures },
      socialMediaUrl: {
        title: t('link'),
        restaurantName: employerRestaurantDetails?.restaurant_name,
        data: {
          ...(employerRestaurantDetails?.website_url
            ? {
                website: {
                  title: 'Website',
                  data: employerRestaurantDetails?.website_url,
                  icon: employerRestaurantDetails?.restaurant_logo ? (
                    <Image
                      src={employerRestaurantDetails.restaurant_logo}
                      alt=""
                      width={24}
                      height={24}
                      loader={customImageLoader}
                    />
                  ) : (
                    <></>
                  ),
                },
              }
            : {}),
          ...(employerRestaurantDetails?.gmaps_url
            ? {
                googleMaps: {
                  title: 'Google Maps',
                  data: employerRestaurantDetails?.gmaps_url,
                  icon: <MapPinIcon className="w-6 h-6" />,
                },
              }
            : {}),
          ...(employerRestaurantDetails?.tripadvisor_url
            ? {
                tripAdvisor: {
                  title: 'TripAdvisor',
                  data: employerRestaurantDetails?.tripadvisor_url,
                  icon: <TripAdvisiorLogo />,
                },
              }
            : {}),
          ...(employerRestaurantDetails?.the_fork_url
            ? { theFork: { title: 'TheFork', data: employerRestaurantDetails?.the_fork_url, icon: <TheForkLogo /> } }
            : {}),
          ...(employerRestaurantDetails?.['linkedin_url']
            ? {
                linkedin_url: {
                  title: 'LinkedIn',
                  data: employerRestaurantDetails?.['linkedin_url'],
                  icon: <LinkedInIcon />,
                },
              }
            : {}),
          ...(employerRestaurantDetails?.['instagram_url']
            ? {
                instagram_url: {
                  title: 'Instagram',
                  data: employerRestaurantDetails?.['instagram_url'],
                  icon: <InstagramIcon />,
                },
              }
            : {}),
          ...(employerRestaurantDetails?.['facebook_url']
            ? {
                facebook_url: {
                  title: 'Facebook',
                  data: employerRestaurantDetails?.['facebook_url'],
                  icon: <FacebookIcon />,
                },
              }
            : {}),
          ...(employerRestaurantDetails?.['tiktok_url']
            ? { tiktok_url: { title: 'TikTok', data: employerRestaurantDetails?.['tiktok_url'], icon: <TitTokIcon /> } }
            : {}),
          ...(employerRestaurantDetails?.['other_social_media_url']
            ? {
                other_social_media_url: {
                  title: t('other_social_media'),
                  data: employerRestaurantDetails?.['other_social_media_url'],
                  icon: <AnySocialLinkIcon />,
                },
              }
            : {}),
        },
      },
    },
    openingHours: { title: t('opening_hours'), data: placesInfo?.opening_hours },
  };
};
